import { useEffect, useMemo, useState } from "react";
import type { Unsubscribe } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { subscribeSessionPublicExercises } from "../services/accessProjectionService";
import {
  listExerciseAnswersForSession,
  updateExerciseRuntimeStatus,
} from "../services/exerciseRuntimeService";
import type {
  ExerciseAnswerRecord,
  ExerciseRuntimeStatus,
  SessionPublicExercise,
} from "../types/access";

const statusLabels: Record<ExerciseRuntimeStatus, string> = {
  active: "Actif",
  correction_visible: "Correction visible",
  not_opened: "Non activé",
  passed: "Passé",
};

function answerStats(answers: ExerciseAnswerRecord[], exerciseId: string) {
  const relatedAnswers = answers.filter(
    (answer) => answer.exerciseRuntimeId === exerciseId,
  );
  const submitted = relatedAnswers.filter(
    (answer) => answer.status === "submitted",
  ).length;
  const drafts = relatedAnswers.length - submitted;

  return {
    drafts,
    submitted,
    total: relatedAnswers.length,
  };
}

export function SessionExerciseRuntimePanel({
  sessionId,
}: {
  sessionId: string | null;
}) {
  const { user } = useAuth();
  const [answers, setAnswers] = useState<ExerciseAnswerRecord[]>([]);
  const [exercises, setExercises] = useState<SessionPublicExercise[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const activeExercises = useMemo(
    () => exercises.filter((exercise) => exercise.runtimeStatus === "active"),
    [exercises],
  );

  useEffect(() => {
    if (!sessionId) {
      setAnswers([]);
      setExercises([]);
      return undefined;
    }

    const unsubscribe: Unsubscribe = subscribeSessionPublicExercises({
      onError: (error) => {
        console.error(error);
        setMessage("Impossible de suivre les exercices live.");
      },
      onNext: setExercises,
      sessionId,
    });

    void listExerciseAnswersForSession(sessionId)
      .then(setAnswers)
      .catch((error: unknown) => {
        console.error(error);
        setMessage("Impossible de charger le suivi exercices.");
      });

    return () => unsubscribe();
  }, [sessionId]);

  async function handleTransition({
    exercise,
    transition,
  }: {
    exercise: SessionPublicExercise;
    transition: "activate" | "pass" | "publish_correction";
  }) {
    if (!user || !sessionId) {
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      await updateExerciseRuntimeStatus({
        actorUid: user.uid,
        exercise,
        sessionId,
        transition,
      });
      setAnswers(await listExerciseAnswersForSession(sessionId));
      setMessage("Statut exercice mis à jour.");
    } catch (error: unknown) {
      console.error(error);
      setMessage("Impossible de modifier cet exercice.");
    } finally {
      setIsSaving(false);
    }
  }

  async function refreshAnswers() {
    if (!sessionId) {
      return;
    }

    try {
      setAnswers(await listExerciseAnswersForSession(sessionId));
    } catch (error: unknown) {
      console.error(error);
      setMessage("Impossible de rafraîchir le suivi exercices.");
    }
  }

  if (!sessionId) {
    return null;
  }

  return (
    <section className="maintenance-block">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Exercices live</p>
          <h3>Activation et suivi</h3>
        </div>
        <button
          className="button button-secondary button-small"
          onClick={() => void refreshAnswers()}
          type="button"
        >
          Rafraîchir
        </button>
      </div>

      {activeExercises.length > 0 ? (
        <div className="alert alert-warning" role="status">
          {activeExercises.length} exercice(s) encore actif(s).
        </div>
      ) : null}

      {exercises.length > 0 ? (
        <ul className="admin-list">
          {exercises.map((exercise) => {
            const stats = answerStats(answers, exercise.id);
            return (
              <li key={exercise.id}>
                <div>
                  <strong>{exercise.title}</strong>
                  <span>
                    {statusLabels[exercise.runtimeStatus]} - mode{" "}
                    {exercise.responseMode} - {stats.submitted} soumise(s),{" "}
                    {stats.drafts} brouillon(s)
                  </span>
                </div>
                <div className="row-actions">
                  <button
                    className="button button-secondary button-small"
                    disabled={isSaving || exercise.runtimeStatus !== "not_opened"}
                    onClick={() =>
                      void handleTransition({
                        exercise,
                        transition: "activate",
                      })
                    }
                    type="button"
                  >
                    Activer
                  </button>
                  <button
                    className="button button-secondary button-small"
                    disabled={isSaving || exercise.runtimeStatus !== "active"}
                    onClick={() =>
                      void handleTransition({
                        exercise,
                        transition: "pass",
                      })
                    }
                    type="button"
                  >
                    Marquer passé
                  </button>
                  <button
                    className="button button-primary button-small"
                    disabled={
                      isSaving ||
                      !(
                        exercise.runtimeStatus === "active" ||
                        exercise.runtimeStatus === "passed"
                      )
                    }
                    onClick={() =>
                      void handleTransition({
                        exercise,
                        transition: "publish_correction",
                      })
                    }
                    type="button"
                  >
                    Correction
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p>Aucun exercice publié dans cette session.</p>
      )}

      {message ? (
        <div className="alert alert-info" role="status">
          {message}
        </div>
      ) : null}
    </section>
  );
}
